/**
 * Самоход календаря, пока занятие в руке: куда ехать, если указатель у края.
 *
 * Своим файлом с 12.09.2026, и причина двойная. `use-calendar-drag.ts` стоял на
 * 337 строках при потолке в 400 (раздел 9 CLAUDE.md), а самоход, оставшийся
 * внутри цикла кадров, не проверялся бы ни одним тестом: тестов на компоненты в
 * проекте нет. Здесь только решение «куда», сама прокрутка и кадры остались в
 * хуке.
 *
 * ⚠️ **Обе оси меряются по ОДНОЙ коробке, и это правда с 17.09.2026 (вечер).**
 * До того дня они мерились по разным: вбок по телу календаря, а вниз и вверх по
 * кромкам ОКНА, потому что вертикально прокручивалась сама страница, календарь
 * по высоте не был обрезан вовсе. В тот вечер экран расписания получил ровно
 * высоту окна и перестал прокручиваться страницей (причина в
 * `docs/schedule-calendar-design.md`, 2.5: содержимое, уезжающее мимо верхней
 * кромки, видно на iPhone в зазоре под строкой состояния, и накрыть его изнутри
 * страницы нечем). Вертикальная прокрутка переехала внутрь календаря, к тому же
 * `.cal__body`, у которого и горизонтальная, и мерить обе оси по разным коробкам
 * стало не только незачем, но и неверно: полоса самохода у нижней кромки ОКНА
 * лежала бы ниже нижней кромки календаря, то есть не срабатывала бы никогда.
 *
 * Разбор в `docs/schedule-drag-design.md`, раздел 14.
 */

/** Полоса у края, в которой начинается самоход. Пиксели. */
export const EDGE_PX = 48;

export interface EdgeDrift {
  /** Календарь вбок: -1 влево, 0 стоит, 1 вправо. */
  x: -1 | 0 | 1;
  /** Календарь вниз-вверх: -1 вверх, 0 стоит, 1 вниз. */
  y: -1 | 0 | 1;
}

/**
 * Куда едет содержимое под неподвижным указателем.
 *
 * ⚠️ **Вертикаль появилась ради пальца** (12.09.2026). У мыши она тоже работает
 * и это единственное, что жест мыши в тот день приобрёл; а вот пальцу без неё
 * нельзя вовсе: на телефоне по умолчанию режим «День», то есть весь перенос
 * вертикальный, а родную прокрутку у пальца на время жеста отбирает сам жест.
 */
export function edgeDrift(
  /** Точка указателя в координатах окна. */
  point: { x: number; y: number },
  /**
   * Коробка ТЕЛА календаря там же, все четыре кромки.
   *
   * ⚠️ Годится сюда ровно `DOMRect` от `.cal__body`, то есть от той самой
   * коробки, которой хук потом двигает `scrollLeft` и `scrollTop`. Кромки чужой
   * коробки (окна, `.cal`, карточки) дали бы полосу самохода не там, где
   * прокрутка, и это ломается молча: самоход просто не включается у края либо
   * включается посреди сетки.
   */
  cal: { left: number; right: number; top: number; bottom: number },
): EdgeDrift {
  return {
    x: point.x < cal.left + EDGE_PX ? -1 : point.x > cal.right - EDGE_PX ? 1 : 0,
    y: point.y < cal.top + EDGE_PX ? -1 : point.y > cal.bottom - EDGE_PX ? 1 : 0,
  };
}
